import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const ISO_DATE_TIME_PATTERN =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d{1,3})?)?(?:Z|[+-]\d{2}:\d{2})$/;

dayjs.extend(utc);
dayjs.extend(timezone);

export type DateISOString = string;

export type CalendarDateParts = {
  readonly year: number;
  readonly month: number;
  readonly day: number;
};

function reportInvalidDatePickerValue(message: string, value: DateISOString) {
  console.error(message, { value });
}

function isValidIsoDatePart(value: string) {
  if (!ISO_DATE_PATTERN.test(value)) {
    return false;
  }

  const [yearString, monthString, dayString] = value.split("-");
  const year = Number(yearString);
  const month = Number(monthString);
  const day = Number(dayString);
  const parsedDate = new Date(Date.UTC(year, month - 1, day));

  return (
    parsedDate.getUTCFullYear() === year &&
    parsedDate.getUTCMonth() === month - 1 &&
    parsedDate.getUTCDate() === day
  );
}

function parseDateISOString(value?: DateISOString) {
  if (value == null) {
    return undefined;
  }

  if (!isValidIsoDatePart(value.slice(0, 10))) {
    reportInvalidDatePickerValue(
      "DatePicker received a non-ISO date string",
      value,
    );
    return undefined;
  }

  const isIsoDateString = ISO_DATE_PATTERN.test(value);
  const isIsoDateTimeString = ISO_DATE_TIME_PATTERN.test(value);

  if (!isIsoDateString && !isIsoDateTimeString) {
    reportInvalidDatePickerValue(
      "DatePicker received a non-ISO date string",
      value,
    );
    return undefined;
  }

  const parsedDate = new Date(value);

  if (Number.isNaN(parsedDate.getTime())) {
    reportInvalidDatePickerValue(
      "DatePicker received an invalid ISO date string",
      value,
    );
    return undefined;
  }

  return parsedDate;
}

export function getLocalCalendarDateParts(date: Date): CalendarDateParts {
  return {
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate(),
  };
}

export function getZonedCalendarDateParts(
  value: DateISOString | undefined,
  timeZone?: string,
): CalendarDateParts | undefined {
  if (value && ISO_DATE_PATTERN.test(value) && isValidIsoDatePart(value)) {
    const [yearString, monthString, dayString] = value.split("-");

    return {
      year: Number(yearString),
      month: Number(monthString),
      day: Number(dayString),
    };
  }

  const parsedDate = parseDateISOString(value);
  if (!parsedDate) return undefined;

  if (!timeZone) {
    return getLocalCalendarDateParts(parsedDate);
  }

  const zonedDate = dayjs(parsedDate).tz(timeZone);
  return {
    year: zonedDate.year(),
    month: zonedDate.month() + 1,
    day: zonedDate.date(),
  };
}

export function createLocalDisplayDate(parts: CalendarDateParts) {
  return new Date(parts.year, parts.month - 1, parts.day, 12);
}

export function serializeDateSelection(
  date: Date | undefined,
  timeZone?: string,
) {
  if (!date) {
    return undefined;
  }

  if (!timeZone) {
    return date.toISOString();
  }

  const { year, month, day } = getLocalCalendarDateParts(date);
  const monthString = String(month).padStart(2, "0");
  const dayString = String(day).padStart(2, "0");

  return dayjs
    .tz(`${year}-${monthString}-${dayString}T00:00:00`, timeZone)
    .toISOString();
}
